import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Table } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { updateNavbarHeading } from "../../../../constants/layout/store/filterSlice";
import {
  DeleteServiceCaller,
  GetServiceCaller,
  PostServiceCaller,
} from "../../../../services/serviceCaller";
import { API_URL } from "../../../../constants";
import { FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import DeleteModal from "../../../../components/DeletePopUpComponent";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

const AssetCategoryForm = () => {
  const { id } = useParams();

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(updateNavbarHeading("Asset Category Master"));
  }, []);

  const initialFormData = {
    Code: "",
    Name: "",
    Description: "",
    IsInActive: 0,
    AssetCode: "",
    Category_type: "",
    brandsLine: [
      {
        ID: "",
        Brand: "",
        Remark: "",
        CategoryID: "",
      },
    ],
  };

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [assettype, setAssetType] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState("");
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    fetchAssetCategory();
  }, []);

  const fetchAssetCategory = async () => {
    try {
      const response = await GetServiceCaller(`${API_URL}asset-category`);
      if (response?.status === 200) {
        const fetchData = response?.data?.data;
        const assetTypeList = Object?.entries(fetchData?.assetType).map(
          ([key, value]) => ({
            value: key,
            label: value,
          })
        );
        setAssetType(assetTypeList);
        setLoading(false);
        setPageLoading(false);
      } else {
        console.error(
          "Failed to fetch asset category. Server responded with status:",
          response.status
        );
      }
    } catch (error) {
      console.error("Error fetching PartnerMaster:", error);
    }
  };

  const schema = Yup.object().shape({
    Name: Yup.string().required("Name is required"),
    AssetCode: Yup.string(),
    Description: Yup.string().nullable(),
    Category_type: Yup.number()
      .typeError("Asset Type is required")
      .required("Asset Type is required")
      .nullable(),
    brandsLine: Yup.array()
      .of(
        Yup.object().shape({
          Brand: Yup.string().required("Make is required"),
        })
      )
      .min(1, "At least one brand line is required"), // Minimum validation for brandsLine
  });

  const validateField = async (fieldName, updatedData) => {
    try {
      // Validate the specific field
      await schema.validateAt(fieldName, updatedData);

      // Clear the error for the specific field if validation is successful
      setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    } catch (err) {
      if (err.path === fieldName) {
        // Update the error only for the specific field
        setErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: err.message,
        }));

        // Show error using toast for specific validation
        if (
          fieldName === "brandsLine" &&
          err.message === "At least one brand line is required"
        ) {
          toast.error("At least one brand line is required", {
            position: "top-right",
          });
        } else if (fieldName.startsWith("brandsLine")) {
          toast.error(err.message, { position: "top-right" });
        }
      } else {
        console.error("Validation error structure is not as expected:", err);
      }
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    const updatedValue = name === "Name" ? value.toUpperCase() : value;
    // Update form data only for fields in the schema
    if (schema.fields[name]) {
      setFormData((prevData) => {
        const updatedData = { ...prevData, [name]: updatedValue };
        validateField(name, updatedData);
        return updatedData;
      });
    }
  };
  const handleBrandChange = async (e, index) => {
    const { name, value } = e.target;

    // Create a copy of the existing brandsLine array and update the specific line
    const updatedLines = [...formData.brandsLine];
    updatedLines[index] = {
      ...updatedLines[index],
      [name]: value,
    };

    // Update the formData with the modified brandsLine
    const updatedFormData = { ...formData, brandsLine: updatedLines };
    setFormData(updatedFormData);

    // Validate the specific field in the 'brandsLine' array
    await validateField(`brandsLine[${index}].${name}`, updatedFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      let response;
      if (id) {
        response = await PostServiceCaller(
          `${API_URL}asset-category/${id}`,
          formData,
          "put"
        );
      } else {
        response = await PostServiceCaller(
          `${API_URL}asset-category`,
          formData,
          "post"
        );
      }

      if (response.status === 200) {
        toast.success("Data submitted successfully");
        setTimeout(() => {
          window.location.href = "/asset-category";
        }, 2000);
      } else {
        console.error(
          "Failed to submit data. Server responded with status:",
          response.status
        );
        console.error("Response data:", response.data);
      }
    } catch (error) {
      if (error.name === "ValidationError") {
        console.error("Validation Error:", error);
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);

        // Display validation errors related to the 'lines' field using toast
        Object.keys(validationErrors).forEach((field) => {
          // Check if the error is related to 'lines' field
          if (field.startsWith("brandsLine")) {
            toast.error(validationErrors[field], { position: "top-right" });
          }
        });
      } else {
        console.error("Unexpected Error:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (type, item) => {
    setShowDeleteModal(true);
    setSelectedDeleteItem(item);
    setDeleteType(type);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setSelectedDeleteItem(null);
    setDeleteType("");
  };

  const handleModalDelete = () => {
    if (selectedDeleteItem) {
      if (deleteType === "BrandLine") {
        handleDeleteRequest(selectedDeleteItem.index, selectedDeleteItem.ID);
      }
      setShowDeleteModal(false);
      setSelectedDeleteItem(null);
      setDeleteType("");
    }
  };

  const handleDeleteRequest = async (index, id) => {
    if (id) {
      try {
        await DeleteServiceCaller(
          `${API_URL}asset-category/${id}`,
          {},
          "delete"
        );
        const updatedLines = formData.brandsLine.filter((_, i) => i !== index);
        setFormData({ ...formData, brandsLine: updatedLines });
      } catch (error) {
        console.error("There was an error deleting the line!", error);
      }
    } else {
      // If PocID is falsy (null, undefined, or empty), just update the lines
      const updatedLines = formData.brandsLine.filter((_, i) => i !== index);
      setFormData({ ...formData, brandsLine: updatedLines });
    }
  };

  const handleAddBrandLine = () => {
    const currentRow =
      formData.brandsLine.length !== 0
        ? formData.brandsLine[formData.brandsLine.length - 1]
        : null;

    // Check if the current row is empty and show a toast if needed
    if (currentRow && currentRow.Brand === "") {
      toast.error("Please fill in the current row.", {
        position: "top-right",
      });
      return;
    }

    // Find the smallest missing line number
    let newLineNo = 1; // Start from 1
    const existingLineNos = formData.brandsLine.map((row) => row.ID);
    // Check if there's any missing number in the sequence
    while (existingLineNos.includes(newLineNo)) {
      newLineNo++; // Increment until we find the first missing line number
    }

    setFormData((prevState) => ({
      ...prevState,
      brandsLine: [
        ...prevState.brandsLine,
        {
          LineNo: newLineNo,
          Brand: "",
          Remark: "",
        },
      ],
    }));
  };

  const handleSelect = (e, name) => {
    const value = e.value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  useEffect(() => {
    if (id) {
      fetchAssetCategoryID(id);
    }
  }, [id]);

  const fetchAssetCategoryID = async (id) => {
    try {
      const response = await GetServiceCaller(`${API_URL}asset-category/${id}`);
      if (response.status === 200) {
        const cleanedData = response?.data?.data;
        const formDataa = {
          Code: cleanedData?.assetCategory?.Code,
          Name: cleanedData?.assetCategory?.Name,
          Description: cleanedData?.assetCategory?.Description,
          IsInActive: cleanedData?.assetCategory?.IsInActive,
          AssetCode: cleanedData?.assetCategory?.AssetCode,
          Category_type: cleanedData?.assetCategory?.Category_type,
          brandsLine: cleanedData?.assetBrand.map((ele, index) => ({
            ID: ele?.ID,
            Brand: ele?.Brand,
            Remark: ele?.Remark,
            CategoryID: ele?.CategoryID,
          })),
        };
        setFormData(formDataa);

        setLoading(false);
      } else {
        console.error(
          "Failed to fetch Asset Category. Server responded with status:",
          response.status
        );
      }
    } catch (error) {
      console.error("Error fetching Asset Category:", error);
    }
  };


  if (pageLoading) {
    return (
      <>
        <p class="position-absolute top-50 start-50 translate-middle text-center">
          <FaSpinner class="spinner" />
        </p>
      </>
    );
  } else {
    return (
      <>
        <div className="Container">
          <form
            onSubmit={handleSubmit}
            id="formOne"
            className="rounded mt-2 bg-white"
          >
            {/** Tab Code Start */}
            <ul className="nav nav-tabs overflow" id="myTab" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link active"
                  style={{ color: "#2F2E4B" }}
                  id="assetCategory-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#assetCategory"
                  type="button"
                  role="tab"
                  aria-controls="assetCategory"
                  aria-selected="true"
                >
                  Asset Category <span className="text-danger"> </span>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link"
                  style={{ color: "#2F2E4B" }}
                  id="brandDetails-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#brandDetails"
                  type="button"
                  role="tab"
                  aria-controls="brandDetails"
                  aria-selected="false"
                >
                  Make Details
                </button>
              </li>
            </ul>
            {/** Tab Code End */}

            <div className="tab-content" id="myTabContent">
              {/** Main Form Code start */}
              <div
                className="tab-pane fade show active"
                id="assetCategory"
                role="tabpanel"
                aria-labelledby="assetCategory-tab"
              >
                <div className="container-fluid mt-2">
                  <div className="row">
                    <div className="col-md-3">
                      <label class="form-label">
                        Asset Type<span className="text-danger">*</span>
                      </label>
                      <Select
                        aria-label="Default select example"
                        id="Category_type"
                        name="Category_type"
                        value={assettype.find(
                          (at) => at.value == formData?.Category_type
                        )}
                        className={` ${errors.Category_type ? "is-invalid" : ""}`}
                        options={[{ value: null, label: "Select" }, ...assettype]}
                        onChange={(e, action) => handleSelect(e, action.name)}
                      />
                      <span class="error">{errors?.Category_type}</span>
                    </div>

                    <div className="col-md-3">
                      <label class="form-label">Code</label>
                      <input
                        readOnly
                        disabled
                        type="text"
                        className="form-control"
                        id="code"
                        name="code"
                        placeholder="Code"
                        value={formData.Code}
                      />
                    </div>

                    <div className="col-md-3">
                      <label class="form-label">
                        Name
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Asset Name"
                        className={`form-control ${errors.Name ? "is-invalid" : ""
                          }`}
                        id="Name"
                        name="Name"
                        value={formData.Name}
                        onChange={(e) => handleChange(e)}
                      />
                      <span class="error">{errors?.Name} </span>
                    </div>

                    <div className="col-md-3">
                      <label class="form-label">Asset Code</label>
                      <input
                        type="text"
                        placeholder="Asset Code"
                        className={`form-control `}
                        id="AssetCode"
                        name="AssetCode"
                        value={formData.AssetCode}
                        onChange={handleChange}
                      />
                      <span class="error"> </span>
                    </div>

                    <div className="col-md-3 ">
                      <label className="form-label">Description</label>
                      <textarea
                        placeholder="Description"
                        className="form-control"
                        name="Description"
                        value={formData.Description}
                        onChange={handleChange}
                      ></textarea>
                    </div>

                    <div className="col-md-3 pt-5">
                      <label className="form-label">Active</label>
                      <input
                        className="form-check-input ms-2 "
                        type="checkbox"
                        name="IsInActive"
                        id="flexSwitchCheckChecked"
                        checked={formData.IsInActive === 1}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            IsInActive: e.target.checked ? 1 : 0,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/** Main Form Code end */}

              {/** Brand Details Code start */}
              <div
                className="tab-pane fade show p-2"
                id="brandDetails"
                role="tabpanel"
                aria-labelledby="brandDetails-tab"
              >
                <div className="table-responsive brand-details rounded mt-2">
                  <button
                    type="button"
                    onClick={handleAddBrandLine}
                    className="custom-btn-sm mb-2 pull-right"
                  >
                    <i class="fa fa-plus"></i>&nbsp;Add
                  </button>
                  <Table
                    style={{ background: "#ECECEC !important" }}
                    // id="poctable_head"
                    className="table table-bordered"
                  >
                    <thead id="thcolor" className="text-center align-middle  ">
                      <tr>
                        <th>Sr. No.</th>
                        <th>
                          Make<span className="text-danger">*</span>
                        </th>
                        <th>Remark</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.brandsLine.map((ele, index) => (
                        <tr>
                          <td className="text-center align-middle">
                            {index + 1}
                          </td>
                          <td>
                            <input
                              type="text"
                              name="Brand"
                              value={ele?.Brand}
                              className={`form-control mx-auto ${errors?.[`brandsLine[${index}].Brand`]
                                ? "is-invalid"
                                : ""
                                }`}
                              onChange={(e) => handleBrandChange(e, index)}
                            />
                            {errors?.[`brandsLine[${index}].Brand`] && ( // Accessing errors for the specific line
                              <div className="invalid-feedback text-center">
                                {errors?.[`brandsLine[${index}].Brand`]}{" "}
                                {/* Displaying the error message */}
                              </div>
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              name="Remark"
                              value={ele?.Remark}
                              className={`form-control mx-auto`}
                              onChange={(e) => handleBrandChange(e, index)}
                            />
                          </td>
                          <td>
                            <div
                              className="text-center"
                              type="button"
                              onClick={() => ele.ID ?
                               null : openDeleteModal("BrandLine", {
                                  index,
                                  ID: ele.ID,
                                }) 
                              }
                            >
                              {/* {formData.lines.length > 1 && ( */}
                              <i
                                className={`${ele.ID ? "disabled-icon bi bi-trash" : "bi bi-trash"}`}
                                style={{ fontSize: "1.2rem" }}
                              ></i>
                              {/* )} */}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/** Brand Details Code end */}
            </div>

            {/* End of dynamic rows */}
            <div className="d-flex justify-content-end">
              <div className="d-flex me-2">
                <div>
                  <button
                    type="button"
                    className="cancel-btn me-2"
                    onClick={() => (window.location.href = "/asset-category")}
                  >
                    Cancel
                  </button>
                </div>
                <div>
                  <button
                    disabled={loading ? true : false}
                    type="submit"
                    className="custom-btn"
                  >
                    {loading ? <FaSpinner class="sm-spinner" /> : "Submit"}
                  </button>
                </div>
              </div>
            </div>
          </form>

          {/** Delete PopUP  */}
          <DeleteModal
            show={showDeleteModal}
            onClose={handleDeleteClose}
            onConfirm={handleModalDelete}
            message="Are you sure you want to delete this item?"
          />
        </div>
      </>
    );
  }

};

export default AssetCategoryForm;
